import { TestOutcomes } from '@flakiness/server/common/stats/testOutcomes.js';
import { TimePeriod } from '@flakiness/server/common/timePeriod.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { humanReadableInteger } from '@flakiness/shared/common/utils.js';
import { consume, ContextType } from '@lit/context';
import * as d3 from 'd3';
import { css, html, LitElement, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { Temporal } from 'temporal-polyfill';
import { contexts } from '../contexts.js';
import { handleLink, Linkifier } from '../linkifier.js';
import { OUTCOME_LABELS, OUTCOME_ORDER, outcomeSwatchFill } from './outcome-legend.js';

/** What the chart draws, apart from the periods themselves. */
export type PeriodChartState = {
  // The granularity of the periods, which decides how the ticks read.
  period: TimePeriod.Period,
  // The outcomes the bars break down into. Stacking nothing plots the period total as a
  // single neutral bar.
  stackedOutcomes: WireTypes.Outcome[],
  // The `since` of the selected period, if any.
  selected?: string,
};

export type PeriodChartLinkifier = Linkifier<PeriodChartState>;

// Stacking runs bottom-up, so reversing severity puts passes at the base and
// regressions on the crest, where a spike is easiest to spot.
const STACK_ORDER: WireTypes.Outcome[] = OUTCOME_ORDER.toReversed();

const TOOLTIP_PADDING = 6;
const TOOLTIP_HEADER_HEIGHT = 15;
const TOOLTIP_ROW_HEIGHT = 13;
const TOOLTIP_SWATCH = 8;
const TOOLTIP_LABEL_X = TOOLTIP_PADDING + TOOLTIP_SWATCH + 6;
const TOOLTIP_COLUMN_GAP = 14;

const BAR_RADIUS = 3;
// The solid edge on top of a total bar, as on the commit history bars.
const BAR_CAP = 2;
// The surface gap between stacked segments.
const SEGMENT_GAP = 1;

const TOTAL_COLOR = 'var(--sl-color-primary-600)';
const TOTAL_HOVER_COLOR = 'var(--sl-color-primary-700)';
const tint = (color: string, white: number) => `color-mix(in oklch, ${color}, white ${white}%)`;
// A step darker than the swatch fill, for the hovered column.
const outcomeHoverFill = (outcome: WireTypes.Outcome) => tint(`var(--fk-color-outcome-${outcome})`, 20);

/** A bar with rounded top corners that sits flat on the baseline. */
function barPath(x: number, y: number, width: number, height: number, radius: number): string {
  const r = Math.max(0, Math.min(radius, width / 2, height));
  return `M${x},${y + height}V${y + r}a${r},${r} 0 0 1 ${r},${-r}H${x + width - r}a${r},${r} 0 0 1 ${r},${r}V${y + height}Z`;
}

type Point = {
  period: WireTypes.DayPeriodSummary,
  since: Temporal.PlainDate,
  until: Temporal.PlainDate,
  /** The label under the bar. */
  label: string,
  /** The label on the second row, set on the first bar of each month or year. */
  group?: string,
  /** The top of the drawn bar, in data units. Anchors the tooltip. */
  value: number,
};

type GSelection = d3.Selection<SVGGElement, unknown, null, undefined>;

/**
 * A label for the days `[since, until]`, e.g. `Sep 7, 2026`, `Sep 6 – 12, 2026` or
 * `Aug 30 – Sep 5, 2026`.
 */
export function rangeLabel(since: Temporal.PlainDate, until: Temporal.PlainDate, options: { year: boolean } = { year: true }): string {
  const monthDay = (day: Temporal.PlainDate) => day.toLocaleString('en-US', { month: 'short', day: 'numeric' });
  const year = (day: Temporal.PlainDate) => options.year ? `, ${day.year}` : '';
  if (since.equals(until))
    return `${monthDay(since)}${year(since)}`;
  if (since.year !== until.year)
    return `${monthDay(since)}${year(since)} – ${monthDay(until)}${year(until)}`;
  const to = since.month === until.month ? String(until.day) : monthDay(until);
  return `${monthDay(since)} – ${to}${year(until)}`;
}

/** The label under a bar: the day a day or week starts on, a month's name, or a quarter. */
function tickLabel(since: Temporal.PlainDate, period: TimePeriod.Period): string {
  if (period === 'day' || period === 'usweek')
    return String(since.day);
  if (period === 'month')
    return since.toLocaleString('en-US', { month: 'short' });
  return `Q${Math.ceil(since.month / 3)}`;
}

const monthAndYear = (day: Temporal.PlainDate) => day.toLocaleString('en-US', { month: 'short', year: 'numeric' });

/**
 * The second-row label of a bar that starts a group: the month for days and weeks, the
 * year for months and quarters. A January names its year too.
 */
function groupLabel(since: Temporal.PlainDate, previous: Temporal.PlainDate|undefined, period: TimePeriod.Period): string|undefined {
  if (period === 'day' || period === 'usweek') {
    if (previous && previous.year === since.year && previous.month === since.month)
      return undefined;
    return since.month === 1 ? monthAndYear(since) : since.toLocaleString('en-US', { month: 'short' });
  }
  return previous?.year === since.year ? undefined : String(since.year);
}

/**
 * The project analytics chart: one bar per period, newest on the right. Every period
 * is a click target, including an empty one: its details list the tests that stopped reporting.
 */
@customElement('period-chart')
class PeriodChart extends LitElement {
  @property({ attribute: false }) periods?: WireTypes.DayPeriodSummary[];
  @property({ attribute: false }) state?: PeriodChartState;
  /** Where picking a period leads. Percentages need a selection of outcomes to mean anything. */
  @property({ attribute: false }) linkifier?: PeriodChartLinkifier;

  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;

  @state() private _width: number = 0;
  @state() private _height: number = 0;

  @query('svg') private _svg?: SVGElement;
  private _resizeObserver?: ResizeObserver;

  override connectedCallback(): void {
    super.connectedCallback();
    this._resizeObserver = new ResizeObserver(entries => {
      this._width = entries[0].contentRect.width;
      this._height = entries[0].contentRect.height;
      this._updateChart();
    });
    this._resizeObserver.observe(this);
  }

  override disconnectedCallback(): void {
    this._resizeObserver?.disconnect();
    this._resizeObserver = undefined;
    super.disconnectedCallback();
  }

  protected override updated(_changedProperties: PropertyValues): void {
    this._updateChart();
  }

  render() {
    return html`<svg></svg>`;
  }

  private _updateChart() {
    const svg = this._svg;
    if (!svg)
      return;

    const root = d3.select(svg);
    // Drop explicit size attrs before measuring so the SVG reflects host sizing.
    root.attr('width', null).attr('height', null);
    if (!this._width || !this._height)
      return;

    root.selectAll('*').remove();
    root.attr('width', this._width).attr('height', this._height);

    const stackKeys = STACK_ORDER.filter(outcome => this.state?.stackedOutcomes.includes(outcome));
    const sumCounts = (counts: WireTypes.OutcomesCount) => stackKeys.length
      ? stackKeys.reduce((sum, outcome) => sum + counts[outcome], 0)
      : TestOutcomes.sumAllCounts(counts);

    const points: Point[] = (this.periods ?? []).map((period, index, all) => {
      const since = Temporal.PlainDate.from(period.since);
      const previous = index > 0 ? Temporal.PlainDate.from(all[index - 1].since) : undefined;
      return {
        period,
        since,
        until: Temporal.PlainDate.from(period.until),
        label: tickLabel(since, this.state?.period ?? 'day'),
        group: groupLabel(since, previous, this.state?.period ?? 'day'),
        value: sumCounts(period.testStats),
      };
    });

    // The bottom fits the two rows of x labels.
    const margin = { top: 12, right: 12, bottom: 40, left: 0 };

    // A selected outcome that never occurs in the window is a state the legend
    // keeps reachable, so every bar can legitimately be zero.
    const peak = d3.max(points, p => p.value) ?? 0;
    const domain: [number, number] = [0, points.length && peak > 0 ? peak * 1.15 : 10];
    const tickFormat = (value: d3.NumberValue) => `${humanReadableInteger(value as number)} tests`;

    // Compute the left margin from the widest y tick label.
    const tmpScale = d3.scaleLinear().domain(domain).nice();
    const tmpG = root.append('g').attr('font-size', '11px');
    tmpScale.ticks(4).forEach(tick => tmpG.append('text').text(tickFormat(tick)));
    margin.left = Math.ceil((tmpG.node() as SVGGElement).getBBox().width) + 12;
    tmpG.remove();

    const width = this._width - margin.left - margin.right;
    const height = this._height - margin.top - margin.bottom;
    if (width <= 0 || height <= 0)
      return;

    const xScale = d3.scaleBand<string>()
      .domain(points.map(p => p.period.since))
      .range([0, width])
      .paddingInner(0.25)
      .paddingOuter(0.1);

    const yScale = d3.scaleLinear()
      .domain(domain)
      .range([height, 0])
      .nice();

    const g = root.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
    // The full step a period owns, padding included.
    const columnX = (point: Point) => xScale(point.period.since)! - xScale.step() * xScale.paddingInner() / 2;

    // A wash behind the hovered column. The selection band is drawn over it, so the
    // selected period keeps its own mark.
    const washes = g.append('g')
      .selectAll('rect')
      .data(points)
      .join('rect')
      .attr('class', 'hover-wash')
      .attr('x', columnX)
      .attr('y', 0)
      .attr('width', xScale.step())
      .attr('height', height);

    // The selected period gets a full-height band behind its bar and a rule on the axis,
    // so it stays visibly linked to the details below the chart.
    const selectedPoint = points.find(p => p.period.since === this.state?.selected);
    if (selectedPoint) {
      const bandX = columnX(selectedPoint);
      g.append('rect')
        .attr('x', bandX)
        .attr('y', 0)
        .attr('width', xScale.step())
        .attr('height', height)
        .attr('fill', 'var(--sl-color-primary-100)');
      g.append('rect')
        .attr('x', bandX)
        .attr('y', height)
        .attr('width', xScale.step())
        .attr('height', 3)
        .attr('fill', 'var(--sl-color-primary-600)');
    }

    // Y-axis grid lines
    g.append('g')
      .call(d3.axisLeft(yScale).ticks(4).tickFormat(tickFormat))
      .call(axis => axis.select('.domain').remove())
      .call(axis => axis.selectAll('.tick line')
        .attr('x2', width)
        .attr('stroke', 'var(--sl-color-neutral-200)')
        .attr('stroke-dasharray', '2,2')
      )
      .call(axis => axis.selectAll('.tick text')
        .attr('fill', 'var(--sl-color-neutral-500)')
        .attr('font-size', '11px')
      );

    const labels = this._appendXAxis(g, points, xScale, height);
    const columns = this._appendBars(g, points, xScale, yScale, height, stackKeys);

    const tooltip = this._createTooltip(g, { width, height, xScale, yScale }, stackKeys);
    const router = this._router;
    const state = this.state;
    const linkifier = this.linkifier;
    const highlight = (point: Point|undefined) => {
      washes.classed('hovered', d => d === point);
      columns.classed('hovered', d => d === point);
      labels.classed('hovered', d => d === point);
    };

    // Full-height hover strips, so a period with a one-pixel bar is still easy to hit.
    g.selectAll('.hit-band')
      .data(points)
      .join('rect')
      .attr('class', 'hit-band')
      .attr('x', columnX)
      .attr('y', 0)
      .attr('width', xScale.step())
      .attr('height', height)
      .attr('fill', 'transparent')
      .attr('cursor', 'pointer')
      .on('mouseenter', (_event: MouseEvent, d: Point) => {
        highlight(d);
        tooltip.show(d);
      })
      .on('mouseleave', () => {
        highlight(undefined);
        tooltip.hide();
      })
      .on('click', (_event: MouseEvent, d: Point) => {
        if (state)
          handleLink(router, linkifier?.({ ...state, selected: d.period.since }));
      });
  }

  /**
   * One column group per period, so the hover state applies to a whole bar. With a
   * selection a column is one segment per outcome, stacked bottom-up in STACK_ORDER;
   * without one it is a single total bar.
   * Each bar part reads its fill from `--fill`, and from `--fill-hover` while its
   * column is hovered.
   */
  private _appendBars(
    g: GSelection,
    points: Point[],
    xScale: d3.ScaleBand<string>,
    yScale: d3.ScaleLinear<number, number>,
    height: number,
    stackKeys: WireTypes.Outcome[],
  ) {
    const bandWidth = xScale.bandwidth();
    const selectedSince = this.state?.selected;
    const columns = g.append('g')
      .selectAll<SVGGElement, Point>('g')
      .data(points)
      .join('g')
      .attr('class', 'column');

    columns.each(function (point) {
      const column = d3.select(this);
      const x = xScale(point.period.since)!;
      if (stackKeys.length) {
        const segments: { outcome: WireTypes.Outcome, y0: number, y1: number }[] = [];
        let y0 = 0;
        for (const outcome of stackKeys) {
          const y1 = y0 + point.period.testStats[outcome];
          if (y1 > y0)
            segments.push({ outcome, y0, y1 });
          y0 = y1;
        }
        segments.forEach((segment, index) => {
          const top = yScale(segment.y1);
          // Every segment but the lowest leaves a surface gap under itself.
          const bottom = yScale(segment.y0) - (index ? SEGMENT_GAP : 0);
          const isTop = index === segments.length - 1;
          column.append('path')
            .attr('class', 'bar')
            .attr('d', barPath(x, top, bandWidth, Math.max(1, bottom - top), isTop ? BAR_RADIUS : 0))
            .style('--fill', outcomeSwatchFill(segment.outcome))
            .style('--fill-hover', outcomeHoverFill(segment.outcome));
        });
      } else if (point.value > 0) {
        const top = yScale(point.value);
        const barHeight = Math.max(1, height - top);
        // The selected period's bar is solid. Any other is a solid rounded cap over a tinted
        // body, the way the commit history bars look. The cap reaches a pixel into the body
        // so no seam shows between them, and no further: anti-aliasing would trace the cap's
        // color along the body's sides.
        const isSelected = point.period.since === selectedSince;
        column.append('path')
          .attr('class', 'bar')
          .attr('d', barPath(x, top, bandWidth, isSelected ? barHeight : Math.min(barHeight, BAR_CAP + 1), BAR_RADIUS))
          .style('--fill', TOTAL_COLOR)
          .style('--fill-hover', TOTAL_HOVER_COLOR);
        if (!isSelected && barHeight > BAR_CAP) {
          column.append('path')
            .attr('class', 'bar')
            .attr('d', barPath(x, top + BAR_CAP, bandWidth, barHeight - BAR_CAP, 0))
            .style('--fill', tint(TOTAL_COLOR, 60))
            .style('--fill-hover', tint(TOTAL_COLOR, 45));
        }
      }
    });

    // A period whose commits have no test results draws no bar, so it gets a
    // baseline tick instead: an untested period and a zero-count period look different.
    g.selectAll('.untested')
      .data(points.filter(p => p.period.commits.total > 0 && p.period.commits.tested === 0))
      .join('rect')
      .attr('class', 'untested')
      .attr('x', d => xScale(d.period.since)!)
      .attr('y', height - 2)
      .attr('width', bandWidth)
      .attr('height', 2)
      .attr('fill', 'var(--sl-color-neutral-200)');

    return columns;
  }

  /**
   * Two rows of x labels: one under each bar, and under those the month or year that
   * each group of bars starts. When the bar labels don't fit side by side, only every
   * few of them show, counted from the selected bar (or the newest one) so that its
   * label always shows; the second row keeps the dates readable.
   */
  private _appendXAxis(g: GSelection, points: Point[], xScale: d3.ScaleBand<string>, height: number) {
    const axis = g.append('g')
      .attr('transform', `translate(0,${height})`)
      .attr('font-size', '11px');

    // Colors and weights come from the stylesheet, which also styles the hovered label.
    const selectedSince = this.state?.selected;
    const center = xScale.bandwidth() / 2;
    const labels = axis.selectAll('text.x-label')
      .data(points)
      .join('text')
      .attr('class', 'x-label')
      .classed('selected', d => d.period.since === selectedSince)
      .attr('x', d => xScale(d.period.since)! + center)
      .attr('y', 17)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .text(d => d.label);

    let widest = 0;
    labels.each(function () {
      widest = Math.max(widest, (this as SVGTextElement).getComputedTextLength());
    });
    const every = Math.max(1, Math.ceil((widest + 4) / xScale.step()));
    const selectedIndex = points.findIndex(point => point.period.since === selectedSince);
    const anchor = selectedIndex >= 0 ? selectedIndex : points.length - 1;
    labels.classed('thinned', (_point, index) => Math.abs(index - anchor) % every !== 0);

    const starts = points.flatMap((point, index) => point.group ? [{ point, index }] : []);
    const groups = axis.selectAll<SVGTextElement, typeof starts[number]>('text.x-group')
      .data(starts)
      .join('text')
      .attr('class', 'x-group')
      .attr('y', 31)
      .attr('font-size', '11px')
      .text(d => d.point.group!);
    // Months name their year on the first label.
    const period = this.state?.period ?? 'day';
    const namesMonths = period === 'day' || period === 'usweek';
    if (namesMonths && starts.length)
      groups.node()!.textContent = monthAndYear(starts[0].point.since);
    // A group label is centered under the group's first bar, but kept between the outer
    // edges of the first and the last bar.
    const left = points.length ? xScale(points[0].period.since)! : 0;
    const right = points.length ? xScale(points[points.length - 1].period.since)! + xScale.bandwidth() : 0;
    const place = (node: SVGTextElement, index: number) => {
      const width = node.getComputedTextLength();
      const x = Math.max(left, Math.min(xScale(starts[index].point.period.since)! + center - width / 2, right - width));
      node.setAttribute('x', String(x));
      return { x, width };
    };
    const boxes = groups.nodes().map((node, index) => ({
      node,
      ...place(node, index),
      bars: (starts[index + 1]?.index ?? points.length) - starts[index].index,
    }));
    // Of two group labels that collide, the one over fewer bars goes. Only the first
    // and the last group can be that short.
    const shown = boxes.slice(0, 1);
    for (const box of boxes.slice(1)) {
      const last = shown[shown.length - 1];
      if (last.x + last.width + 6 <= box.x) {
        shown.push(box);
      } else if (last.bars < box.bars) {
        last.node.remove();
        shown[shown.length - 1] = box;
      } else {
        box.node.remove();
      }
    }
    // If the first label went, the next one names the year instead. That one opens a
    // whole month, so the longer label still fits before the label after it.
    if (namesMonths && shown.length && shown[0] !== boxes[0]) {
      const index = boxes.indexOf(shown[0]);
      shown[0].node.textContent = monthAndYear(starts[index].point.since);
      place(shown[0].node, index);
    }
    return labels;
  }

  private _createTooltip(g: GSelection, frame: {
    width: number,
    height: number,
    xScale: d3.ScaleBand<string>,
    yScale: d3.ScaleLinear<number, number>,
  }, stackKeys: WireTypes.Outcome[]) {
    const { width, height, xScale, yScale } = frame;
    const tooltip = g.append('g').attr('class', 'tooltip').style('display', 'none').style('pointer-events', 'none');
    const rect = tooltip.append('rect')
      .attr('rx', 4).attr('ry', 4)
      .attr('fill', 'var(--sl-color-neutral-800)');
    const body = tooltip.append('g');

    return {
      show: (point: Point) => {
        body.selectAll('*').remove();
        const testStats = point.period.testStats;
        const totalTests = TestOutcomes.sumAllCounts(testStats);
        // With a selection the rows are exactly the selected outcomes, zeroes
        // included: an explicitly picked series reading 0 is itself the answer.
        const outcomes = stackKeys.length
          ? OUTCOME_ORDER.filter(outcome => stackKeys.includes(outcome))
          : OUTCOME_ORDER.filter(outcome => testStats[outcome] > 0);

        const header = body.append('text')
          .attr('x', TOOLTIP_PADDING)
          .attr('y', TOOLTIP_PADDING + 11)
          .attr('fill', 'white')
          .attr('font-size', '11px')
          .attr('font-weight', '600')
          .text(rangeLabel(point.since, point.until));
        const subheader = body.append('text')
          .attr('x', TOOLTIP_PADDING)
          .attr('y', TOOLTIP_PADDING + 11 + TOOLTIP_ROW_HEIGHT)
          .attr('fill', 'var(--sl-color-neutral-400)')
          .attr('font-size', '11px')
          .text(point.period.commits.total === 0 ? 'No commits' : point.period.commits.tested === 0 ? 'Not tested' : `${totalTests.toLocaleString()} tests`);

        let contentWidth = Math.max(header.node()!.getComputedTextLength(), subheader.node()!.getComputedTextLength());
        const counts: SVGTextElement[] = [];
        const rowsTop = TOOLTIP_PADDING + TOOLTIP_HEADER_HEIGHT + TOOLTIP_ROW_HEIGHT;
        outcomes.forEach((outcome, index) => {
          const baseline = rowsTop + index * TOOLTIP_ROW_HEIGHT + 10;
          body.append('rect')
            .attr('x', TOOLTIP_PADDING)
            .attr('y', baseline - TOOLTIP_SWATCH)
            .attr('width', TOOLTIP_SWATCH)
            .attr('height', TOOLTIP_SWATCH)
            .attr('rx', 1)
            .style('fill', outcomeSwatchFill(outcome));
          const label = body.append('text')
            .attr('x', TOOLTIP_LABEL_X)
            .attr('y', baseline)
            .attr('fill', 'var(--sl-color-neutral-300)')
            .attr('font-size', '11px')
            .text(OUTCOME_LABELS[outcome]);
          const count = body.append('text')
            .attr('y', baseline)
            .attr('fill', 'white')
            .attr('font-size', '11px')
            .attr('text-anchor', 'end')
            .text(testStats[outcome].toLocaleString());
          contentWidth = Math.max(contentWidth, (TOOLTIP_LABEL_X - TOOLTIP_PADDING)
            + label.node()!.getComputedTextLength() + TOOLTIP_COLUMN_GAP + count.node()!.getComputedTextLength());
          counts.push(count.node()!);
        });

        const tooltipWidth = contentWidth + TOOLTIP_PADDING * 2;
        const tooltipHeight = rowsTop + outcomes.length * TOOLTIP_ROW_HEIGHT + TOOLTIP_PADDING;
        for (const count of counts)
          count.setAttribute('x', String(tooltipWidth - TOOLTIP_PADDING));
        rect.attr('width', tooltipWidth).attr('height', tooltipHeight);

        // The tooltip can be nearly as tall as the plot, so keep it off the hovered
        // column: flip to whichever side has room.
        const x = xScale(point.period.since)! + xScale.bandwidth() / 2;
        let tx = x > width / 2 ? x - tooltipWidth - 12 : x + 12;
        tx = Math.max(0, Math.min(tx, Math.max(0, width - tooltipWidth)));
        const ty = Math.max(0, Math.min(yScale(point.value) - tooltipHeight - 10, height - tooltipHeight));
        tooltip.attr('transform', `translate(${tx},${ty})`).style('display', null);
      },
      hide: () => tooltip.style('display', 'none'),
    };
  }

  static styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      flex: auto;
      align-self: stretch;
      min-width: 0;
      min-height: 0;
    }
    svg {
      display: block;
      width: 100%;
      height: 100%;
    }
    .hover-wash {
      fill: var(--sl-color-neutral-100);
      opacity: 0;
    }
    .hover-wash.hovered {
      opacity: 1;
    }
    .bar {
      fill: var(--fill);
    }
    .column.hovered .bar {
      fill: var(--fill-hover);
    }
    .x-label {
      fill: var(--sl-color-neutral-500);
    }
    .x-label.selected {
      fill: var(--sl-color-primary-700);
      font-weight: 600;
    }
    .x-label.hovered:not(.selected) {
      fill: var(--sl-color-neutral-800);
      font-weight: 600;
    }
    .x-label.thinned {
      display: none;
    }
    .x-group {
      fill: var(--sl-color-neutral-600);
      font-weight: 600;
    }
  `;
}
